import type {
  AwardSummary,
  HomeRecognition,
  Me,
  PoolPositions,
  RecognizeOptions,
  StockAdminQueue,
  TeamSnapshot,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { homeLayout } from '../home/layout';
import { myAwardsQuery } from './awards';
import { anyAwardsQuery } from './homes';
import { queueQuery } from './ops';
import { budgetsQuery, recognitionQuery, recognizeOptionsQuery } from './server';

/** `first-run` shows the first-day states (development builds only, for design review). */
export type HomeView = 'live' | 'first-run';

/** Everything Home shows besides Waiting on you (which the bell shares: `waitingQuery`). */
export interface HomeData {
  /** Pool positions this person may see, or `null` when Home has no pool card for them. */
  positions: PoolPositions | null;
  options: RecognizeOptions;
  /** `GET /api/v1/home/recognition`. */
  recognition: HomeRecognition;
  /** `GET /api/v1/queue?view=queue` for the Stock Admin team (the queue is their Home), else `null`. */
  queue: StockAdminQueue | null;
  /**
   * A pre-approved requester's own Executive Discretionary Bonus requests this year (`GET
   * /api/v1/awards/mine`: open drafts and what was sent), for R1's hero; `null` for everyone else.
   */
  requests: { year: number; items: AwardSummary[]; earlier: boolean } | null;
  /**
   * The Stock Admin team: whether any award has ever reached them, so an empty queue is "all caught
   * up" rather than its first day. `null` for everyone else, or when it can't be read.
   */
  queueHistory: boolean | null;
}

function holds(me: Me, ...roles: Me['roles'][number]['role'][]): boolean {
  return me.roles.some((a) => roles.includes(a.role));
}

/** A team before anyone is recognized: the same people, nothing awarded. */
function firstDayTeam(team: TeamSnapshot): TeamSnapshot {
  return {
    ...team,
    recognized: 0,
    repeat: 0,
    notYet: team.people,
    months: team.months.map((m) => ({ ...m, awards: 0, cashCents: 0 })),
    rows: team.rows.map((row) => ({
      ...row,
      months: row.months.map(() => 0),
      awards: 0,
      status: 'none' as const,
    })),
    mostRecognized: [],
    notRecognized: {
      count: team.people,
      sample: team.rows.map((row) => row.person).slice(0, 6),
      biggestTeams: [],
    },
  };
}

const NONE = { cashCents: 0, options: 0 };

/** Every pool at its full budget: nothing paid or in progress yet. */
function firstDayPositions(positions: PoolPositions | null): PoolPositions | null {
  if (positions === null) return null;
  return {
    ...positions,
    pools: positions.pools.map((p) => ({
      ...p,
      paid: NONE,
      inProgress: NONE,
      remaining: p.budget,
    })),
  };
}

/**
 * The first day, built from the live answers (design review of the empty states): nothing held,
 * nobody recognized, no awards of one's own, every pool at its full budget, an empty queue that
 * nothing has reached yet. Budgets and people stay as they are.
 */
export function firstDay(data: HomeData): HomeData {
  const { recognition, queue } = data;
  return {
    ...data,
    positions: firstDayPositions(data.positions),
    recognition: {
      ...recognition,
      sectors: recognition.sectors.map((sector) => ({
        ...sector,
        holds: [],
        team: sector.team === null ? null : firstDayTeam(sector.team),
      })),
      mine: {
        ...recognition.mine,
        awards: [],
        totals: { awards: 0, since: null, options: 0, cashPaidCents: 0 },
        spot: { ...recognition.mine.spot, nominations: [] },
      },
    },
    queue:
      queue === null
        ? null
        : {
            ...queue,
            views: queue.views.map((v) => ({ ...v, count: 0 })),
            steps: queue.steps.map((step) => ({ ...step, count: 0 })),
            rows: [],
            mine: { checks: 0, letters: 0 },
            shareworksRows: 0,
            hiddenOwn: [],
          },
    requests: data.requests === null ? null : { ...data.requests, items: [], earlier: false },
    queueHistory: data.queueHistory === null ? null : false,
  };
}

/**
 * Home's data for this person: pool positions (`GET /api/v1/budgets`), Recognize options, the
 * recognition cards (`GET /api/v1/home/recognition`) and, for the Stock Admin team, the queue
 * (`GET /api/v1/queue?view=queue`, the same cache entry as the queue page). The role Homes add
 * only what their hero needs; each card below the hero loads its own read.
 */
export function homeQuery(me: Me, view: HomeView = 'live') {
  return queryOptions({
    queryKey: ['home', 'data', me.person.id, view] as const,
    queryFn: async ({ client }): Promise<HomeData> => {
      // Not a requester's: Budget pools refuses them. R1 reads its own Executive pool (APP-054).
      const needsPools = holds(
        me,
        'sector_lead',
        'stock_admin',
        'executive',
        'finance',
        'system_admin',
      );
      const queueHome = homeLayout(me.roles) === 'queue';
      const [positions, options, recognition, queue, requests, queueHistory] = await Promise.all([
        needsPools ? client.query(budgetsQuery) : Promise.resolve(null),
        client.query(recognizeOptionsQuery),
        client.query(recognitionQuery),
        queueHome ? client.query(queueQuery('queue')) : Promise.resolve(null),
        // An Executive's Home is X1. Their delegate's is R1: what they've entered for them.
        holds(me, 'executive_delegate') && !holds(me, 'executive')
          ? client.query(myAwardsQuery).then((mine) => ({
              year: mine.year,
              items: [...mine.drafts, ...mine.submitted.items],
              // Anything in an earlier year: not their first day, even with nothing this year.
              earlier: mine.years.some((y) => y.year !== mine.year && y.count > 0),
            }))
          : Promise.resolve(null),
        // The hero words an empty queue by it; it never holds the Home up if it can't be read.
        queueHome ? client.query(anyAwardsQuery).catch(() => null) : Promise.resolve(null),
      ]);
      const data: HomeData = { positions, options, recognition, queue, requests, queueHistory };
      return view === 'first-run' ? firstDay(data) : data;
    },
    staleTime: 60_000,
  });
}
